import React from 'react';
import { useQueryClient } from 'react-query';
import { LaundryIcon } from 'assets/icons/brikks/LaundryIcon';
import laundrySrc from 'assets/media/laundry.png';
import { CircularImage } from 'components/brikks/sub-components/CircularImage';
import { LinkBrikkContent } from 'components/brikks/sub-components/LinkBrikkContent';
import { ShouldRender } from 'components/ShouldRender';
import { Brikk } from 'components/Brikk';
import Draggable from 'components/drag-and-drop/Draggable';
import { BrikkInfoModal } from 'components/modals/BrikkInfoModal';
import { InfoButton } from 'components/form-elements/buttons/InfoButton';
import { SeeMoreButton } from 'components/form-elements/buttons/SeeMoreButton';
import LaundryServiceModal from 'components/brikks/home/LaundryService/components/LaundryServiceModal';
import { useLaundryLink } from 'services/laundry/link';
import { getBaseUrl } from 'utils/http';
import { BRE, GRE } from 'global/tenant-config/tenants';
import { getTenantIDBasedOnURL } from 'global/tenant-config';
import { BrikkModal } from 'components/modals/BrikkModal';
import { useLaundryResources } from 'services/laundry/resources';
import { LaundryBrikkContent } from './components/LaundryBrikkContent';

const LAUNDRY_LINK = 'laundryLink';

export function LaundryService({ position, status, code, name }) {
  const tenantId = getTenantIDBasedOnURL();
  const { laundryLink, isLoading, isError } = useLaundryLink(tenantId === GRE);
  const {
    isLoading: isResourcesLoading,
    isError: isResrourcesError,
    laundryResources,
  } = useLaundryResources(tenantId !== GRE);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const laundryPreviewLink = React.useMemo(
    () => (tenantId !== BRE ? getBaseUrl(laundryLink) : 'aptus.se'),
    [tenantId, laundryLink],
  );

  const handleOpenModal = (e) => {
    e.stopPropagation();
    queryClient.invalidateQueries(LAUNDRY_LINK);
    setIsModalOpen(true);
  };

  function handleInfoModal(e) {
    e.stopPropagation();
    setIsInfoModalOpen(!isInfoModalOpen);
  }

  return (
    <ShouldRender condition={!isError && !isResrourcesError}>
      <Draggable position={position}>
        <Brikk
          isLoading={isLoading || isResourcesLoading}
          brikkKey={code}
          name={name}
          status={status}
        >
          <Brikk.Header
            icon={<LaundryIcon />}
            heading="laundryBrikk"
            status={status}
            brikkKey={code}
          />

          <Brikk.Content>
            <CircularImage
              src={laundrySrc}
              alt="Illustrations of a washing machine"
            />

            <ShouldRender condition={tenantId !== GRE}>
              <LinkBrikkContent link={laundryPreviewLink} />
            </ShouldRender>
          </Brikk.Content>

          <Brikk.Button>
            <SeeMoreButton
              onClick={handleOpenModal}
              brikkNameIntl="laundryBrikk"
            />

            <InfoButton
              showModal={isInfoModalOpen}
              onClick={(e) => handleInfoModal(e)}
            />
          </Brikk.Button>
        </Brikk>
      </Draggable>

      <BrikkInfoModal
        title="Laundry service info"
        showModal={isInfoModalOpen}
        onClick={() => setIsInfoModalOpen(!isInfoModalOpen)}
      />

      {tenantId !== GRE ? (
        <LaundryServiceModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
        />
      ) : (
        <BrikkModal
          isOpen={isModalOpen}
          heading="laundryBrikk"
          onClose={() => {
            setIsModalOpen(false);
          }}
        >
          <LaundryBrikkContent laundryResources={laundryResources} />
        </BrikkModal>
      )}
    </ShouldRender>
  );
}
